"use strict";
// Email preferences — the one screen where somebody says whether we may
// send them marketing email.
//
// Two things this screen is careful about.
//
// It never implies that the address exists simply because the account
// does. An account signed in only through Google or Discord has no
// verified email address at all, so there is nothing to subscribe; that
// case gets a plain explanation and a route to the Email screen, not a
// toggle that would fail on click.
//
// And it never lets marketing read as account email. Receipts, sign-in
// codes and security notices are not a preference and are not switched
// off here, so the screen says so where somebody deciding will see it,
// not in small print underneath.
//
// The toggle is optimistic in appearance only: it moves, the request
// goes, and a failure puts it back where it was. Leaving it showing a
// preference the backend never recorded is the one outcome worth
// writing extra code to avoid.
(function () {
    var ARROW_BACK_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>';
    var MAIL_ICON = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="5" width="19" height="14" rx="2.5"/><path d="m3.5 7 8.5 6 8.5-6"/></svg>';
    var LOCK_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="11" rx="2.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>';
    function t(hubEl, key, fallback) {
        return hubEl.dataset[key] || fallback;
    }
    function backButtonHtml(hubEl) {
        return ('<button type="button" data-flix-hub-nav="settings" style="display:inline-flex;align-items:center;gap:8px;background:none;border:none;color:var(--dim);font-size:13px;font-weight:600;cursor:pointer;padding:0;margin-bottom:20px" style-hover="color:var(--text)">' +
            '<span aria-hidden="true" data-flix-hub-back-arrow style="display:flex">' +
            ARROW_BACK_ICON +
            "</span>" +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nBackToSettings", "Back to settings")) +
            "</button>");
    }
    /** The switch itself. `aria-pressed` carries the state because this is a button that toggles, not a link. */
    function toggleHtml(hubEl, on) {
        var track = on ? "var(--accent)" : "var(--surface2)";
        var knobSide = on ? "calc(100% - 22px)" : "2px";
        return ('<button type="button" data-flix-marketing-toggle aria-pressed="' +
            (on ? "true" : "false") +
            '" aria-label="' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingToggle", "Marketing emails")) +
            '" style="flex:none;position:relative;width:46px;height:26px;border-radius:26px;border:1px solid var(--border);background:' +
            track +
            ';cursor:pointer;padding:0;transition:background .2s">' +
            '<span aria-hidden="true" data-flix-marketing-knob style="position:absolute;top:1px;inset-inline-start:' +
            knobSide +
            ';width:20px;height:20px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.35);transition:inset-inline-start .2s"></span>' +
            "</button>");
    }
    function rowHtml(input) {
        return ('<div style="display:flex;align-items:center;gap:16px;padding:18px 16px">' +
            '<div style="flex:1;min-width:0">' +
            '<div style="font-size:14px;font-weight:700;color:var(--text);margin-bottom:4px">' +
            window.FlixHubFormat.escapeHtml(input.title) +
            "</div>" +
            '<div style="font-size:12.5px;color:var(--dim);line-height:1.5">' +
            window.FlixHubFormat.escapeHtml(input.description) +
            "</div>" +
            "</div>" +
            input.control +
            "</div>");
    }
    /**
     * What the account has actually said, and when.
     *
     * Shown only when there is something real to show. A line reading
     * "Subscribed on —" for an address with no consent record would be
     * inventing a fact about a permission.
     */
    function historyHtml(hubEl, status) {
        var locale = document.documentElement.lang || "en";
        var lines = [];
        if (status.consentAt) {
            lines.push(window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingConsentedOn", "You opted in on %%date%%").replace("%%date%%", window.FlixHubFormat.date(status.consentAt, locale))));
        }
        if (status.unsubscribedAt) {
            lines.push(window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingUnsubscribedOn", "You opted out on %%date%%").replace("%%date%%", window.FlixHubFormat.date(status.unsubscribedAt, locale))));
        }
        if (lines.length === 0)
            return "";
        return ('<p data-flix-marketing-history style="margin:14px 2px 0;font-size:12px;color:var(--faint);line-height:1.7">' +
            lines.join("<br>") +
            "</p>");
    }
    /** The account-email note. Not small print: somebody deciding needs it at the point of deciding. */
    function transactionalNoteHtml(hubEl) {
        return ('<div style="display:flex;gap:12px;align-items:flex-start;margin-top:18px;padding:14px 16px;border-radius:14px;background:var(--surface2);border:1px solid var(--border)">' +
            '<span aria-hidden="true" style="flex:none;color:var(--faint);display:flex;margin-top:1px">' +
            LOCK_ICON +
            "</span>" +
            '<p style="margin:0;font-size:12.5px;color:var(--dim);line-height:1.6">' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingTransactional", "Order confirmations, sign-in codes and security alerts are always sent. They are not marketing and this setting does not affect them.")) +
            "</p>" +
            "</div>");
    }
    /**
     * An account with no verified address.
     *
     * There is genuinely nothing to manage, so the screen says exactly
     * that and offers the step that would change it, rather than a
     * disabled switch that explains nothing.
     */
    function notManageableHtml(hubEl) {
        return ('<div data-flix-marketing-unavailable style="border-radius:var(--radius-md);background:var(--surface);border:1px solid var(--border);padding:24px 20px;text-align:center">' +
            '<span aria-hidden="true" style="display:inline-flex;color:var(--faint);margin-bottom:10px">' +
            MAIL_ICON +
            "</span>" +
            '<div style="font-family:var(--display);font-weight:800;font-size:16px;color:var(--text);margin-bottom:6px">' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingNoEmailTitle", "No email address on this account")) +
            "</div>" +
            '<p style="margin:0 0 18px;font-size:13px;color:var(--dim);line-height:1.6">' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingNoEmailMessage", "You signed in without an email address, so there is nothing to send to yet. Add and verify one to choose what you receive.")) +
            "</p>" +
            '<button type="button" data-flix-hub-nav="email" style="height:44px;padding:0 22px;border-radius:var(--radius-button);border:none;background:linear-gradient(135deg,var(--accent),var(--accent2));color:#160d05;font-family:var(--display);font-weight:800;font-size:13px;cursor:pointer;transition:transform .2s" style-hover="transform:translateY(-2px)">' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingAddEmail", "Add an email address")) +
            "</button>" +
            "</div>");
    }
    function screenHtml(hubEl, status) {
        var body = status.manageable
            ? '<div data-flix-marketing-card style="border-radius:var(--radius-md);background:var(--surface);border:1px solid var(--border);overflow:hidden">' +
                rowHtml({
                    title: t(hubEl, "i18nMarketingToggle", "Marketing emails"),
                    description: t(hubEl, "i18nMarketingToggleHint", "New releases, offers and occasional news. You can turn this off at any time."),
                    control: toggleHtml(hubEl, status.eligible),
                }) +
                "</div>" +
                '<div data-flix-marketing-feedback></div>' +
                historyHtml(hubEl, status) +
                transactionalNoteHtml(hubEl)
            : notManageableHtml(hubEl) + transactionalNoteHtml(hubEl);
        return ('<div style="max-width:520px">' +
            backButtonHtml(hubEl) +
            '<h2 style="font-family:var(--display);font-weight:800;font-size:22px;color:var(--text);margin:0 0 6px">' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingTitle", "Email preferences")) +
            "</h2>" +
            '<p style="margin:0 0 22px;font-size:13.5px;color:var(--dim);line-height:1.6">' +
            window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingIntro", "Choose whether we may send you marketing email.")) +
            "</p>" +
            body +
            "</div>");
    }
    async function render(hubEl, contentEl) {
        contentEl.innerHTML = window.FlixHubRender.loadingState(t(hubEl, "i18nLoading", "Loading…"));
        try {
            var status = await window.FlixAuthClient.getMarketing();
            contentEl.innerHTML = screenHtml(hubEl, status);
        }
        catch (err) {
            contentEl.innerHTML = window.FlixHubRender.errorState({
                title: t(hubEl, "i18nErrorTitle", "Something went wrong"),
                message: t(hubEl, "i18nMarketingLoadError", "We couldn't load your email preferences."),
                retryLabel: t(hubEl, "i18nRetry", "Retry"),
            });
            window.flixCompileHoverStyles(contentEl);
            return;
        }
        window.flixCompileHoverStyles(contentEl);
    }
    function paint(toggle, on) {
        toggle.setAttribute("aria-pressed", on ? "true" : "false");
        toggle.style.background = on ? "var(--accent)" : "var(--surface2)";
        var knob = toggle.querySelector("[data-flix-marketing-knob]");
        if (knob)
            knob.style.insetInlineStart = on ? "calc(100% - 22px)" : "2px";
    }
    async function toggle(hubEl, toggleEl) {
        if (toggleEl.dataset.busy === "true")
            return;
        var was = toggleEl.getAttribute("aria-pressed") === "true";
        var next = !was;
        toggleEl.dataset.busy = "true";
        toggleEl.style.opacity = "0.6";
        paint(toggleEl, next);
        var feedbackEl = document.querySelector("[data-flix-marketing-feedback]");
        if (feedbackEl)
            feedbackEl.innerHTML = "";
        try {
            var result = await window.FlixAuthClient.updateMarketing(next);
            // Painted from what the backend recorded, not from what was
            // clicked — they are the same in every expected case, and when
            // they are not, the backend is right.
            paint(toggleEl, result.eligible);
            if (feedbackEl) {
                feedbackEl.innerHTML =
                    '<p data-flix-marketing-saved style="margin:12px 2px 0;font-size:12.5px;color:var(--success,#4caf7d)">' +
                        window.FlixHubFormat.escapeHtml(result.eligible
                            ? t(hubEl, "i18nMarketingOnSaved", "You'll receive marketing email.")
                            : t(hubEl, "i18nMarketingOffSaved", "You've been unsubscribed.")) +
                        "</p>";
            }
        }
        catch (err) {
            // Back to where it was. A switch left showing a preference that
            // was never recorded is worse than an error message.
            paint(toggleEl, was);
            if (feedbackEl) {
                feedbackEl.innerHTML =
                    '<p data-flix-marketing-error style="margin:12px 2px 0;font-size:12.5px;color:var(--error)">' +
                        window.FlixHubFormat.escapeHtml(t(hubEl, "i18nMarketingSaveError", "Couldn't save that. Please try again.")) +
                        "</p>";
            }
        }
        finally {
            toggleEl.dataset.busy = "false";
            toggleEl.style.opacity = "";
        }
    }
    window.FlixHubMarketing = { render: render, toggle: toggle };
})();
